import type {
  Clinic,
  ClinicDTO,
  ClinicImages,
  ClinicImagesDTO,
  ContactInformation,
  ContactInformationDTO,
} from "@akina/domain";
import { type Mapper } from "@akina/infra";
import { decodeHtmlTextFromString } from "@akina/utils";
import type { ClinicTreatmentRead, ClinicTreatmentReadApi } from "types/schemas";

export const clinicMapper: Mapper<Clinic, ClinicDTO> = {
  toDomain: clinicDto => ({
    id: clinicDto.id,
    name: decodeHtmlTextFromString(clinicDto.name),
    address: decodeHtmlTextFromString(clinicDto.address),
    country: clinicDto.country,
    city: clinicDto.city,
    bookingUrl: clinicDto.booking_url,
    websiteUrl: clinicDto.website_url,
    lat: clinicDto.coordinates?.latitude ?? 0,
    long: clinicDto.coordinates?.longitude ?? 0,
    specialities: clinicDto.specialities,
    phoneNumber: clinicDto.phone_number,
    contactInformation: contactInformationMapper.toDomain(clinicDto.contact_information),
  }),
  toDTO: clinicDomain => ({
    id: clinicDomain.id,
    name: clinicDomain.name,
    address: clinicDomain.address,
    country: clinicDomain.country,
    city: clinicDomain.city,
    booking_url: clinicDomain.bookingUrl,
    website_url: clinicDomain.websiteUrl,
    specialities: clinicDomain.specialities,
    phone_number: clinicDomain.phoneNumber,
    coordinates: {
      latitude: clinicDomain.lat ?? 0,
      longitude: clinicDomain.long ?? 0,
    },
    contact_information: contactInformationMapper.toDTO(clinicDomain.contactInformation),
  }),
};

export const clinicImagesMapper: Mapper<ClinicImages, ClinicImagesDTO> = {
  toDomain: clinicImagesDto => ({
    id: clinicImagesDto.id,
    name: clinicImagesDto.name,
    imagesUrls: clinicImagesDto.images_urls ?? [],
    profileImageUrl: clinicImagesDto.profile_image_url,
  }),
  toDTO: clinicImagesDomain => ({
    id: clinicImagesDomain.id,
    name: clinicImagesDomain.name,
    images_urls: clinicImagesDomain.imagesUrls,
    profile_image_url: clinicImagesDomain.profileImageUrl,
  }),
};

export const clinicTreatmentMapper = {
  toDomain: (clinicTreatmentDto: ClinicTreatmentReadApi): ClinicTreatmentRead => ({
    prescriptionStatus: clinicTreatmentDto.prescription_status,
    nextPrescriptionStatus: clinicTreatmentDto.next_prescription_status,
    hasPrescription: clinicTreatmentDto.has_prescription,
    treatmentId: clinicTreatmentDto.treatment_id,
    treatmentApplicationStatus: clinicTreatmentDto.treatment_application_status,
    patientId: clinicTreatmentDto.patient_id,
    firstName: decodeHtmlTextFromString(clinicTreatmentDto.first_name),
    lastName: decodeHtmlTextFromString(clinicTreatmentDto.last_name),
    profilePictureUrl: clinicTreatmentDto.profile_picture_url,
    genderAtBirth: clinicTreatmentDto.gender_at_birth,
    age: clinicTreatmentDto.age,
    goal: clinicTreatmentDto.goal ?? null,
    weekInTreatmentSinceStart: clinicTreatmentDto.week_in_treatment_since_start,
    diagnosis: clinicTreatmentDto.diagnosis ?? null,
    modifiedAt: clinicTreatmentDto.modified_at,
    lastCompletedSessionDate: clinicTreatmentDto.last_completed_session_date
      ? new Date(clinicTreatmentDto.last_completed_session_date)
      : null,
    createdAt: clinicTreatmentDto.created_at,
    therapistId: clinicTreatmentDto.therapist_id ?? null,
    organizationId: clinicTreatmentDto.organization_id ?? null,
    treatmentTemplateKey: clinicTreatmentDto.treatment_template_key,
    phases: {
      currentPhaseKey: clinicTreatmentDto.phases.current_phase_key,
      phasePlans: clinicTreatmentDto.phases.phase_plans.map(p => ({
        startWeek: p.start_week,
        endWeek: p.end_week,
        key: p.key,
      })),
      phaseLogs: clinicTreatmentDto.phases.phase_logs
        ? Object.fromEntries(
            Object.entries(clinicTreatmentDto.phases.phase_logs).map(([key, log]) => [
              key,
              {
                phaseKey: log.phase_key,
                startDate: log.start_date ? new Date(log.start_date) : null,
                endDate: log.end_date ? new Date(log.end_date) : null,
              },
            ]),
          )
        : {},
    },
    numCompletedSessionsInLastTwoWeeks: clinicTreatmentDto.num_completed_sessions_in_last_two_weeks,
    lastReminderDate: clinicTreatmentDto.last_reminder_date ? new Date(clinicTreatmentDto.last_reminder_date) : null,
    lastInvitedDate: clinicTreatmentDto.last_invited_date ? new Date(clinicTreatmentDto.last_invited_date) : null,
    patientState: clinicTreatmentDto.patient_state,
    startDate: new Date(clinicTreatmentDto.start_date),
    scheduledEndDate: new Date(clinicTreatmentDto.scheduled_end_date),
    treatmentStatus: clinicTreatmentDto.treatment_status,
    streakActive: clinicTreatmentDto.streak_active,
    reasonNonExtendable: clinicTreatmentDto.reason_non_extendable,
  }),
};

export const contactInformationMapper = {
  toDomain: (contactInformationDto: ContactInformationDTO) => ({
    useWhatsappChatbot: contactInformationDto?.use_whatsapp_chatbot ?? true,
    useDefaultAkinaContact: contactInformationDto?.use_default_akina_contact ?? true,
    customEmail: contactInformationDto?.custom_email ?? "",
    customTelephone: contactInformationDto?.custom_telephone ?? "",
  }),
  toDTO: (contactInformation: ContactInformation) => ({
    use_whatsapp_chatbot: contactInformation?.useWhatsappChatbot ?? true,
    use_default_akina_contact: contactInformation?.useDefaultAkinaContact ?? true,
    custom_email: contactInformation?.customEmail ?? "",
    custom_telephone: contactInformation?.customTelephone ?? "",
  }),
};
